import { animate, createTimeline, stagger, splitText, createDrawable, spring } from '../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, SUPPORTS_CLIP, resolveDuration, resolveStagger, IS_TOUCH, scrollTrigger, BLUR } from './motion-tokens.js';

/**
 * MOTION COMPLEXITY SINGLETON
 * El orquestador lo setea antes del primer render basándose en el tono del sitio.
 * 'full'    → todas las variantes (split, clipPath, SVG draw)
 * 'reduced' → solo fade + stagger (sitios formales/corporativos)
 */
let _motionComplexity = 'full';
export const setMotionComplexity = (c) => { _motionComplexity = c; };
export const getMotionComplexity = ()  => _motionComplexity;

// VARIANT ANIMATIONS (Fase 1E)
// Registry de micro-animaciones por bloque+variante.
// Cada entry es una función pura (el) que ejecuta animaciones sobre el bloque.
// La duración base viene del BLOCK_ENERGY definido en la spec.
// Regla de oro: 1 movimiento PRIMARIO + soporte secundario. Nunca 3 protagonistas.

// --- HERO (extraído a variants/hero.js) ---
import { heroV1, heroV2, heroV3 } from './variants/hero.js';

// --- TIMELINE (extraído a variants/timeline.js) ---
import { timelineSticky, timelineV2, timelineV3 } from './variants/timeline.js';

// --- FEATURES (extraído a variants/features.js) ---
import { featuresV1, featuresV2, featuresV3 } from './variants/features.js';

// --- PRICING (extraído a variants/pricing.js) ---
import { pricingV1, pricingV2, pricingV3 } from './variants/pricing.js';

// --- REGISTRY CENTRAL ---

// --- TESTIMONIALS (extraído a variants/testimonials.js) ---
import { testimonialsV1, testimonialsV2, testimonialsV3 } from './variants/testimonials.js';

// --- STATS (extraído a variants/stats.js) ---
import { statsV1, statsV2, statsV3, statsV4 } from './variants/stats.js';

// --- REGISTRY CENTRAL ---

// --- FAQ (BLOCK_ENERGY: MEDIA — DURATION.slow + EASE.refined) ---

// --- FAQ (extraído a variants/faq.js) ---
import { faqV1, faqV2, faqV3 } from './variants/faq.js';

// --- TEAM (extraído a variants/team.js) ---
import { teamV1, teamV2, teamV3 } from './variants/team.js';

// --- PROCESS (extraído a variants/process.js) ---
import { processV1, processV2, processV3 } from './variants/process.js';

// --- JOURNEY (Track B · Ola 1.3 — extraído a variants/journey.js) ---
import { journeyEntrance } from './variants/journey.js';

// --- GALLERY (extraído a variants/gallery.js) ---
import { galleryV1, galleryV2, galleryV3, galleryV4 } from './variants/gallery.js';

// --- BENTO (extraído a variants/bento.js) — una coreografía para v1/v2/v3 (render único) ---
import { bentoMosaic } from './variants/bento.js';

// --- CONTENT (extraído a variants/content.js) ---
import { contentV1, contentV2, contentV3 } from './variants/content.js';

// --- LOGOS (extraído a variants/logos.js) ---
import { logosV1, logosV2 } from './variants/logos.js';

// --- MAPS (extraído a variants/maps.js) ---
import { mapsV1, mapsV2 } from './variants/maps.js';

// --- BANNER & CTA (extraído a variants/banner.js) ---
import { ctaBar, bannerAnim } from './variants/banner.js';

// --- MISC & INDIVIDUALS (extraído a variants/misc.js) ---
import { countdownAnim, comparisonAnim, beforeAfterAnim, videoAnim, newsletterAnim, footerAnim, trustAnim, reviewsAnim, carruselAnim, imagenAnim } from './variants/misc.js';

// --- TRACK C: FORM · MENU · SERVICES (extraído a variants/newblocks.js) ---
import { formAnim, menuAnim, servicesAnim } from './variants/newblocks.js';

// REGLA DE COBERTURA (la vigila tests/test_variant_animation_coverage.py):
// TODA variante de block_meta.json debe resolver aquí por la cascada de getVariantAnimation
// (`tipo-vN` → `tipo` → null). Si no resuelve cae al fallback genérico del orquestador, que
// es el camino que dejó el <p> de content-v4 congelado semi-invisible en el PUBLICADO
// (gotcha nº18) — y en el editor no se nota, porque ahí todo se fuerza visible.
// Cómo cubrir una variante nueva:
//   · su layout coincide con el de una hermana (variante CSS-only sobre el mismo DOM)
//     → ALIAS a la coreografía hermana ('features-v4': featuresV1). DRY, cero código nuevo.
//   · su layout cambia de verdad → función propia en variants/ ('stats-v4', 'gallery-v4').
//   · todas las variantes comparten un render único → clave SIN variante ('bento', 'form').

// --- RESERVA EN VIVO (extraído a variants/reserva-live.js) ---
import { reservaLiveAnim } from './variants/reserva-live.js';
import { entradasAnim } from './variants/entradas.js';
// --- TIENDA (ofertas, novedades, más vendidos, sueles comprar) ---
import { tiendaAnim } from './variants/tienda.js';

export const VARIANT_ANIMATIONS = {
    // Hero
    'hero-v1': heroV1,
    'hero-v2': heroV2,
    'hero-v3': heroV3,
    // v4 ('Marco editorial') es CSS puro sobre el layout centrado de v1/v2 (ver blocks/hero.js:
    // solo v3 tiene rama propia) → reusa la coreografía de v1 (título + subtítulo + CTA).
    'hero-v4': heroV1,
    // Timeline
    // v1 (zigzag) y v2 (línea izquierda) comparten coreografía: `timelineV2` anima
    // `.tl-line`/`.tl-dot`/`.tl-card`, que son exactamente las piezas del markup de la v1
    // (la v2 las pinta con estilo inline). Antes la v1 llevaba el SCROLL-JACK — el panel
    // sticky —, y como es la variante por defecto se lo comía toda web generada por la IA:
    // en el editor se veía normal (la coreografía es `previewOnly`) y en la web publicada
    // salía una tarjeta suelta con media pantalla vacía. El scroll-jack sigue disponible,
    // pero hay que PEDIRLO: es la v4.
    'timeline-v1': timelineV2,
    'timeline-v2': timelineV2,
    'timeline-v3': timelineV3,
    'timeline-v4': timelineSticky,
    // Features
    'features-v1': featuresV1,
    'features-v2': featuresV2,
    'features-v3': featuresV3,
    // v4 ('Glass flotante') = las tarjetas de v1 reestilizadas por CSS → misma coreografía.
    'features-v4': featuresV1,
    // Pricing
    'pricing-v1': pricingV1,
    'pricing-v2': pricingV2,
    'pricing-v3': pricingV3,
    // v4 ('Glass flotante') = las tarjetas de v1 con cristal/glow por CSS → misma coreografía
    // (pricingV1 detecta el plan destacado por su scale inline, que v4 conserva).
    'pricing-v4': pricingV1,
    // Testimonials
    'testimonials-v1': testimonialsV1,
    'testimonials-v2': testimonialsV2,
    'testimonials-v3': testimonialsV3,
    // v4 ('Glass flotante') = las tarjetas de v1 con borde gradiente por CSS → misma coreografía.
    'testimonials-v4': testimonialsV1,
    // Stats
    'stats-v1': statsV1,
    'stats-v2': statsV2,
    'stats-v3': statsV3,
    // v4 ('Tablero KPI'): el CSS convierte la FILA del render por defecto en una REJILLA de
    // tiles → layout distinto de verdad, coreografía propia (cascada diagonal + badge + cifra).
    'stats-v4': statsV4,
    // FAQ
    'faq-v1': faqV1,
    'faq-v2': faqV2,
    'faq-v3': faqV3,
    // v4 ('Acordeón glass'): el CSS lleva el grid de v3 a UNA columna de tarjetas plegables →
    // la apertura izq→der en dos mitades de v3 no encaja; reusa la de v1 (lista vertical).
    'faq-v4': faqV1,
    // Team
    'team-v1': teamV1,
    'team-v2': teamV2,
    'team-v3': teamV3,
    // Process
    'process-v1': processV1,
    'process-v2': processV2,
    'process-v3': processV3,
    // Journey (Track B · Ola 1.3) — el scroll-draw lo gestiona journey-scroll.js.
    // Clave SIN variante: todas comparten la misma entrada, y así cubre también la 'v3'
    // que quedó horneada en sitios viejos (block_meta solo declara v1 y v2).
    'journey': journeyEntrance,
    // Gallery
    'gallery-v1': galleryV1,
    'gallery-v2': galleryV2,
    'gallery-v3': galleryV3,
    // v4 ('Banner editorial'): el CSS apila el DOM de v3 en banner + tira de miniaturas →
    // layout distinto de verdad, coreografía propia (cortina + Ken Burns + cascada lateral).
    'gallery-v4': galleryV4,
    // Carrusel — clave SIN variante: v1..v4 comparten DOM (la diferencia la ponen el CSS y el
    // modo de Splide), así que una sola coreografía cubre las cuatro por la cascada
    // `tipo-vN` → `tipo` de getVariantAnimation. Anima SOLO la primera diapositiva: del resto
    // se encarga Splide (y en 'loop' hay clones, que no deben animarse dos veces).
    'carrusel': carruselAnim,
    // Imagen suelta: clave sin variante (v1 a sangre y v2 contenida comparten coreografía).
    'imagen': imagenAnim,
    // Bento — render ÚNICO CSS-driven (v1 mosaico · v2 editorial · v3 glass): una sola
    // coreografía para las tres, con clave sin variante.
    'bento': bentoMosaic,
    // Content
    'content-v1': contentV1,
    'content-v2': contentV2,
    'content-v3': contentV3,
    // content-v4 ('Editorial flotante') renderiza el layout de v1 (texto izq. + img.,
    // ver blocks/content.js), así que reutiliza la coreografía de contentV1. Sin esto
    // caía al fallback genérico frágil del orquestador y su <p> quedaba congelado/invisible.
    'content-v4': contentV1,
    // Logos
    'logos-v1': logosV1,
    'logos-v2': logosV2,
    // Catálogo: comparte el render de logos (mediaStripBlock de blocks/logos.js) con los
    // MISMOS selectores (.logos-track / .logo-item) → alias 1:1, sin coreografía nueva.
    'catalogo-v1': logosV1,
    'catalogo-v2': logosV2,
    // Maps
    'maps-v1': mapsV1,
    'maps-v2': mapsV2,
    // CTA-Bar
    'cta-bar': ctaBar,
    // Countdown
    'countdown': countdownAnim,
    // Banner
    'banner': bannerAnim,
    // Comparison
    'comparison': comparisonAnim,
    // Before-After
    'before-after': beforeAfterAnim,
    // Video
    'video': videoAnim,
    // Newsletter (bloque suelto, legacy) + banda newsletter = form-v3 (reusa la misma coreografía)
    'newsletter': newsletterAnim,
    'form-v3': newsletterAnim,
    // Footer
    'footer': footerAnim,
    // Track C — clave sin variante (v1/v2 comparten coreografía)
    'form': formAnim,
    'menu': menuAnim,
    'services': servicesAnim,
    // Quick-wins: reservas reusa la coreografía del form (mismos .form-field);
    // trust y reseñas traen la suya (clave sin variante → v1/v2 la comparten).
    'reservas': formAnim,
    'trust': trustAnim,
    'reviews': reviewsAnim,
    // Reserva en vivo: clave SIN variante (v1/v2 comparten coreografía). Registrarla es
    // OBLIGATORIO, no cosmético: sin entrada aquí caería al fallback genérico, que puede
    // congelar un tween y dejar el widget semi-invisible en el publicado (gotcha #18).
    'reserva-live': reservaLiveAnim,
    'entradas': entradasAnim,
    // Tienda: clave SIN variante (todas comparten coreografía: solo el título y el aviso; las
    // tarjetas las pinta el runtime al llegar los datos). Ver variants/tienda.js.
    'ofertas': tiendaAnim,
    'novedades': tiendaAnim,
    'top': tiendaAnim,
    'habitual': tiendaAnim,
};

/**
 * Lookup: busca animación específica para bloque+variante.
 * Estrategia de búsqueda:
 * 1. 'blockType-variant' → bloques con múltiples variantes (ej: 'hero-v1')
 * 2. 'blockType' solo    → bloques sin variantes (ej: 'cta-bar', 'banner', 'video')
 * Si ninguna coincide → null → el orchestrador aplica el fallback genérico de data-anim.
 */
export function getVariantAnimation(blockType, variant) {
    return VARIANT_ANIMATIONS[`${blockType}-${variant}`]
        || VARIANT_ANIMATIONS[blockType]
        || null;
}
